/**
 * useRaceDebugState — polls live race state at ~4Hz and returns a snapshot
 * for the RaceDebugOverlay to render.
 *
 * Gathers from:
 *   - ServiceRegistry → EntitySystem → RaceClientHandler (hazards, powerups)
 *   - GmEventService module singleton (phase, announcement, racePlayerState)
 *   - RaceGateStore module singleton (ability gate)
 *   - window.nm + tracer (connection, ping, room)
 *   - RaceDebugOverlayState (rolling event log)
 *
 * Returns null when the overlay should not render (not a race map, debug
 * surface off, or master toggle off) — the component uses this to early-return.
 */

import { useEffect, useState } from 'react';
import { ServiceRegistry } from '../../../core/ServiceRegistry';
import { getGmEventState } from '../../../services/GmEventService';
import { RaceGateStore } from '../../../systems/race/raceGateStore';
import {
  isRaceDebugOverlayEnabled,
  getRaceDebugLog,
} from '../../../systems/race/RaceDebugOverlayState';

const RACE_MAP_PREFIX = 'gm_race_';
const POLL_MS = 250;

export interface RaceDebugSnapshot {
  mapId: string;
  /** Local player's schema gmLevel (null = schema not readable yet). */
  localGmLevel: number | null;
  gmState: ReturnType<typeof getGmEventState>;
  hazards: Array<{ id: string; kind: string; x: number; y: number; width: number; height: number }>;
  powerups: Array<{ id: string; kind: string; x: number; y: number }>;
  gateActive: boolean;
  gateAllowedCount: number | null;
  log: ReturnType<typeof getRaceDebugLog>;
  nm: any;
  room: any;
  tracer: any;
}

function onRaceMap(): boolean {
  const mapId: unknown = (window as any).nm?.getRoom?.()?.state?.mapId;
  return typeof mapId === 'string' && mapId.startsWith(RACE_MAP_PREFIX);
}

export function useRaceDebugState(): RaceDebugSnapshot | null {
  const [, forceUpdate] = useState(0);
  const [wasEnabled, setWasEnabled] = useState(isRaceDebugOverlayEnabled());

  // Poll at ~4Hz for live state reads. The interval restarts when the
  // toggle flips so we don't miss the "turned on" transition (the old
  // empty-deps version never started the interval if the overlay was off
  // on first render, leaving the panel stale until an external re-render).
  const nowEnabled = isRaceDebugOverlayEnabled();
  // Off a race map the overlay renders nothing, so the poll re-renders only
  // when the map turns into (or out of) a race map (B7: it re-rendered the
  // hidden overlay 4 times a second on every map).
  useEffect(() => {
    if (!nowEnabled) return;
    setWasEnabled(true);
    let wasRace = onRaceMap();
    const interval = setInterval(() => {
      const race = onRaceMap();
      if (race || race !== wasRace) forceUpdate((n) => n + 1);
      wasRace = race;
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [nowEnabled]);

  // Force a re-render when the toggle flips on (covers the gap before the
  // interval's first tick).
  if (nowEnabled !== wasEnabled) {
    setWasEnabled(nowEnabled);
    if (nowEnabled) forceUpdate((n) => n + 1);
  }

  // Gate 1: master toggle (ON by default via RACE_DEBUG.defaultEnabled,
  // controlled by the DebugToolbox 🏁 button). The debug-surface gate is
  // handled by DevConfig.systems.debugToolbox which controls whether the
  // toolbox (and its toggle) renders at all.
  if (!isRaceDebugOverlayEnabled()) return null;

  const registry = ServiceRegistry.getInstance();
  const entitySystem = registry.get('EntitySystem');
  const raceHandler = entitySystem?.getRaceClientHandler?.() ?? null;
  const nm = (window as any).nm;
  const room = nm?.getRoom?.();
  const mapId: string = room?.state?.mapId ?? '';

  // Gate 2: only on race maps.
  if (!mapId.startsWith(RACE_MAP_PREFIX)) return null;

  const gateAllowed = RaceGateStore.getAllowedCount();

  // Local player's schema gmLevel — Start Race gates on gmLevel >= 5, so
  // "no lobby / no Start button" questions are self-diagnosable here
  // (DJ ask, 2026-07-09: a cached guest/dev JWT silently costs admin).
  const selfSchema = room?.state?.players?.get?.(room?.sessionId);
  const localGmLevel: number | null =
    typeof selfSchema?.gmLevel === 'number' ? selfSchema.gmLevel : null;

  return {
    mapId,
    localGmLevel,
    gmState: getGmEventState(),
    hazards: raceHandler?.getHazards() ?? [],
    powerups: raceHandler?.getPowerups() ?? [],
    gateActive: RaceGateStore.isActive(),
    gateAllowedCount: gateAllowed,
    log: getRaceDebugLog(),
    nm,
    room,
    tracer: nm?.tracer,
  };
}
